import {
  FormSubmissionComment, FormSubmissionConfig,
  FormattedFormSubmittionComment, GetFormSubmissionsCommentsMeta
} from '@modules/forms/types';
import ShowMoreComments
  from '@modules/social/components/feed-item/message/show-more-comments';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import * as userSelector from '@modules/core/selectors/logged-user';
import { useAppSelector } from '@common/hooks';
import { AsyncList } from '@common/components/list';
import { ApiResponse } from '@common/services/api/types';
import { UnboundCommentComponent } from '@modules/social/components/comment';

type SubmissionCommentsListProps = {
  comments: FormattedFormSubmittionComment[] | null;
  submissionConfig: FormSubmissionConfig;
  onDelete: (commentId: string) => Promise<void>;
  getComments: () => Promise<
    ApiResponse<FormSubmissionComment[], GetFormSubmissionsCommentsMeta>
  >;
};

const SubmissionCommentsList = memo(({
  comments,
  submissionConfig,
  onDelete,
  getComments
}: SubmissionCommentsListProps) => {
  const { t } = useTranslation();
  const loggedUser = useAppSelector(userSelector.selected);

  const disableInitialFetch = comments !== null;

  const items = comments !== null ? comments : [];
  if (comments !== null && comments.length === 0) {
    return (
      <div className="List List--empty">
        {t('common:no_comments_yet')}
      </div>
    );
  }

  const asyncListItems = comments !== null ? items : undefined;

  return (
    <AsyncList
      order="DESC"
      items={asyncListItems}
      disableInitialFetch={disableInitialFetch}
      data={{
        // @ts-expect-error
        onFetch: getComments,
        filter: {
          id: submissionConfig.id,
        },
      }}
      renderRow={UnboundCommentComponent}
      rowProps={{
        loggedUser,
        onDelete,
        message: submissionConfig,
      }}
      placeholder={t('common:no_comments_yet')}
      ShowMoreComponent={ShowMoreComments}
    />
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.comments === nextProps.comments &&
    prevProps.submissionConfig.id === nextProps.submissionConfig.id &&
    prevProps.onDelete === nextProps.onDelete &&
    prevProps.getComments === nextProps.getComments
  );
});

export default SubmissionCommentsList;
